/**
 * Collapses runs of consecutive reasoning steps that share an identical
 * `description`. For each run, the last step is kept (carries the most
 * recent `completed` value) and renumbered so the returned array reads
 * 1..N with no gaps.
 *
 * See docs/superpowers/specs/2026-05-22-chain-of-thought-dedup-design.md.
 *
 * Pure. Input is never mutated.
 */

export interface DedupableStep {
  step: number
  description: string
  completed: boolean
  /** FS-659: present ⇒ the step opens onto its own distinct content. */
  detail?: unknown
}

export function dedupReasoningSteps<S extends DedupableStep>(steps: readonly S[]): S[] {
  if (!Array.isArray(steps) || steps.length === 0) return []

  const kept: S[] = []
  for (const step of steps) {
    const last = kept[kept.length - 1]
    if (
      last !== undefined &&
      typeof step.description === 'string' &&
      typeof last.description === 'string' &&
      step.description === last.description &&
      // Collapsing exists to hide the legacy path's repeated PHASE labels, which
      // carry nothing. Two steps that each open onto their own sources and
      // result are different steps that happen to share a label — merging them
      // would delete one real call from the record (e.g. two `db_ask` lookups
      // both labelled "Looked up the plant data").
      step.detail === undefined &&
      last.detail === undefined
    ) {
      kept[kept.length - 1] = step
    } else {
      kept.push(step)
    }
  }

  return kept.map((step, index) => ({ ...step, step: index + 1 }))
}
